/**
 * Phase 8 · C-12 — `useBillingBalance()` TanStack Query hook.
 *
 * Powers the Sidebar trial card and Topbar credits pill. Read-only; 60s
 * staleTime; no refetchOnWindowFocus so sidebar/topbar don't thrash on tab
 * switches.
 */
import { useQuery } from '@tanstack/react-query';
import { billingService, type BillingBalance } from './billingService';

export interface UseBillingBalanceResult extends BillingBalance {
  isLoading: boolean;
  isError: boolean;
}

const EMPTY: BillingBalance = {
  balance: 0,
  daysLeft: 0,
  isTrialActive: false,
  planSlug: 'trial',
};

export function useBillingBalance(orgId: string | undefined): UseBillingBalanceResult {
  const q = useQuery<BillingBalance>({
    queryKey: ['billingBalance', orgId],
    enabled: Boolean(orgId),
    queryFn: () => billingService.getBalance(orgId as string),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const data = q.data ?? EMPTY;
  return {
    balance: data.balance,
    daysLeft: data.daysLeft,
    isTrialActive: data.isTrialActive,
    planSlug: data.planSlug,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
